import React, { useState, useEffect, useRef, useMemo, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { storage } from '../firebase'; // Adjust the import path as needed
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { PencilIcon, TrashIcon, PhotoIcon, CheckIcon, XMarkIcon, QuestionMarkCircleIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import { ArrowUpTrayIcon, InformationCircleIcon } from '@heroicons/react/20/solid';
import { processImage } from '../utils/imageProcessor'; // Add this import
import AssetLibraryPicker from '../components/AssetLibraryPicker';
import { fieldToCategoryMap, assetLibrary, resolveLibraryUrl } from '../data/assetLibrary';
import { isProceduralBackground, getProceduralKey } from './proceduralBackgrounds';
import ProceduralThumbnail from '../components/ProceduralThumbnail';
import customAssetsService from '../services/customAssetsService';
import { useUser } from '../userContext';

/** Resolve a field URL to something displayable (procedural → thumbnail). */
function resolvePreviewUrl(url) {
  if (!url || !isProceduralBackground(url)) return url;
  const asset = assetLibrary.find(a => a.url === url);
  return asset?.thumbnail ? resolveLibraryUrl(asset.thumbnail) : '';
}

/**
 * =============================================================================
 * FILE UPLOAD TOOLTIP STANDARD
 * =============================================================================
 * 
 * When using FileUpload, follow this standard for consistent UX:
 * 
 * PROP          | PURPOSE                    | EXAMPLE
 * --------------|----------------------------|----------------------------------
 * displayText   | What it is                 | "Cover Image", "Logo", "Background"
 * subtitle      | Brief format hint          | "JPG or PNG, square format"
 * tooltip       | Full specs + where used    | "200x200px recommended. Shown on start screen and share cards."
 * 
 * TOOLTIP FORMAT:
 * "[Dimensions] recommended. [Where it appears / How it behaves]."
 * 
 * EXAMPLES:
 * - Logo:       subtitle="PNG or SVG, square format"
 *               tooltip="200x200px recommended. Shown on start screen, results, and social share cards."
 * 
 * - Background: subtitle="JPG or PNG, seamless horizontal tile"  
 *               tooltip="1920x1080px+ recommended (<500KB). Height fills screen, tiles horizontally."
 * 
 * - Cover:      subtitle="JPG or PNG, landscape format"
 *               tooltip="1200x630px recommended (1.91:1 ratio). Displayed on the start screen above your game title."
 * 
 * - Character:  subtitle="PNG or SVG, square format"
 *               tooltip="100x100px recommended. Your player avatar in the game."
 * =============================================================================
 */

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

// Public (non-admin) editor only surfaces the brand logo upload. Every other
// custom asset upload (background, character sprites, obstacles, collectibles,
// share image, cover, etc.) is hidden from public users but still rendered for
// admins so internal/advanced flows keep working.
const PUBLIC_FIELD_ALLOWLIST = new Set([
  'headlinePhoto',
  'coverPhoto',
  'sharingPhoto',
  'backgroundImage',
  'collectibleImage',
  'obstacleImage',
  'ballImage',
  'blockImage',
  'flagImage',
  'wallImage',
  'snakeHeadImage',
  'snakeBodyImage',
  'pipeImage',
  'pipeImageBody',
  'pipeImageEnd',
  'image',
]);

// Sanitize filename for Firebase Storage path and URLs (avoids broken uploads/backgrounds with special chars)
function sanitizeStorageFileName(name) {
  if (!name || typeof name !== 'string') return 'file';
  const base = name.replace(/[/\\#?\[\]*%&+<>'"]/g, '_').replace(/\s+/g, '_').trim();
  return base || 'file';
}

const FileUpload = ({ 
  onSuccess, 
  onDelete, 
  field, 
  options, 
  displayText, 
  displayPreview, 
  subtitle = null, 
  recommended = false, 
  maxDimensions,
  acceptedFileTypes = "image/jpeg, image/png, image/svg+xml", // Default to include SVG
  isPublicGame = false, // Add this prop
  icon, // Add icon prop for public games
  compact = false, // Add compact mode prop
  tooltip = null, // Add tooltip prop
  showLibrary = true, // Show library option by default
  gameType = null, // Current game type for filtering library assets
  companyId = null, // Company ID for saving custom assets
  selectionNameKey = null, // When set, store library selection name (e.g. 'gameBackgroundImageName') and show it in the label
  fullLibrary = false, // When true (e.g. in Advanced section), show full asset library with sidebar and My Assets instead of category-only
  syncOfferOtherField = null, // When set (e.g. 'gameBackgroundImage'), after setting this field show "Also update [label]?" dialog
  syncOfferOtherLabel = null, // Human label for the other field, e.g. "Game background"
  allowMultipleSelect = false, // When true, library opens in multi-select mode; use with appendToArrayField
  appendToArrayField = null, // When set (e.g. 'playerAnimationFrames'), multi-select appends urls to this array and onSuccess receives full options
  appendToArrayMaxLength = null, // When set (e.g. 16), appended array is capped to this length
}) => {
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  // Show a brief success state after an upload completes so the user gets
  // an explicit "yes that worked" — the progress bar alone disappears
  // silently and leaves people uncertain.
  const [justUploadedName, setJustUploadedName] = useState('');
  const successTimerRef = useRef(null);
  const [currentFileUrl, setCurrentFileUrl] = useState(options?.[field] || '');
  const [errorMessage, setErrorMessage] = useState('');
  const fileInputRef = useRef(null);
  const [fileType, setFileType] = useState('');
  const [showAssetLibrary, setShowAssetLibrary] = useState(false);
  const pickerRef = useRef(null);
  const [syncOfferOpen, setSyncOfferOpen] = useState(false);
  const [syncOfferPendingOptions, setSyncOfferPendingOptions] = useState(null);

  // Check if this field has library assets available
  const hasLibraryAssets = showLibrary && fieldToCategoryMap[field];
  // Same logic for all file uploads: when using library, store/display selection name (explicit key or derived as field + "Name")
  const effectiveSelectionNameKey = selectionNameKey ?? (hasLibraryAssets ? `${field}Name` : null);
  // Only restrict to one category (hide sidebar) for player/character fields  -  so users can't pick platform groups as character. Obstacles, collectibles, etc. get full library with sidebar. Override when fullLibrary (e.g. Advanced section) so user sees all assets + My Assets.
  const restrictToCategoryOnly = !fullLibrary && hasLibraryAssets && fieldToCategoryMap[field] === 'player';

  // Resolve asset name from library when URL is set but name is missing (covers defaults and legacy data)
  const resolvedAssetName = useMemo(() => {
    if (effectiveSelectionNameKey && options?.[effectiveSelectionNameKey]) return options[effectiveSelectionNameKey];
    const url = options?.[field];
    if (!url || !effectiveSelectionNameKey) return null;
    const match = assetLibrary.find(a => a.url === url || a.url?.endsWith(url?.replace(/^.*\/assets\/library/, '')) || url?.endsWith(a.url?.replace(/^.*\/assets\/library/, '')));
    return match?.name || null;
  }, [effectiveSelectionNameKey, options?.[effectiveSelectionNameKey], options?.[field], field]);

  // When setting a background field, optionally show "Also update the other background?" and apply one or both.
  // Only skip the dialog when we explicitly set the other field in this same action (e.g. group had both), not when the other field was already in options.
  const applyOrOfferSync = (updatedOptions, otherFieldSetInThisUpdate = false) => {
    if (syncOfferOtherField && syncOfferOtherLabel && updatedOptions[field]) {
      if (otherFieldSetInThisUpdate) {
        onSuccess(updatedOptions);
        return;
      }
      setSyncOfferPendingOptions(updatedOptions);
      setSyncOfferOpen(true);
      return;
    }
    onSuccess(updatedOptions);
  };

  const handleSyncOfferConfirm = (alsoUpdateOther) => {
    const next = { ...syncOfferPendingOptions };
    if (alsoUpdateOther && syncOfferPendingOptions && syncOfferPendingOptions[field]) {
      next[syncOfferOtherField] = syncOfferPendingOptions[field];
    }
    setSyncOfferOpen(false);
    setSyncOfferPendingOptions(null);
    onSuccess(next);
  };

  // Handle asset selection from library
  const handleLibrarySelect = (asset) => {
    // Multi-select confirmation: append urls to array field and pass full options
    if (appendToArrayField && asset.multiple && Array.isArray(asset.urls)) {
      const existing = options?.[appendToArrayField] || [];
      let merged = [...existing, ...asset.urls];
      if (appendToArrayMaxLength != null) merged = merged.slice(0, appendToArrayMaxLength);
      const updatedOptions = { ...options, [appendToArrayField]: merged };
      if (merged[merged.length - 1]) setCurrentFileUrl(merged[merged.length - 1]);
      onSuccess(updatedOptions);
      setShowAssetLibrary(false);
      return;
    }
    // Check if this is a group selection
    if (asset.isGroup && asset.assets) {
      // Apply all assets from the group
      const updatedOptions = { ...options };
      Object.entries(asset.assets).forEach(([fieldKey, url]) => {
        updatedOptions[fieldKey] = url;
      });
      // Update the current field's URL for preview
      if (asset.assets[field]) {
        setCurrentFileUrl(asset.assets[field]);
      }
      // Only skip "also update other background?" when the group included the other field
      const otherSetInGroup = !!(syncOfferOtherField && asset.assets[syncOfferOtherField]);
      applyOrOfferSync(updatedOptions, otherSetInGroup);
    } else {
      // Single asset selection
      setCurrentFileUrl(asset.url);
      const updatedOptions = { ...options, [field]: asset.url };
      if (effectiveSelectionNameKey && asset.name) updatedOptions[effectiveSelectionNameKey] = asset.name;
      else if (effectiveSelectionNameKey) updatedOptions[effectiveSelectionNameKey] = null;
      applyOrOfferSync(updatedOptions);
    }
    setShowAssetLibrary(false);
  };

  useEffect(() => {
    if (options?.[field]) {
      setCurrentFileUrl(options[field]);
      // Try to determine if the current file is an SVG
      if (options[field]?.includes('data:image/svg+xml') || options[field]?.endsWith('.svg')) {
        setFileType('svg');
      }
    } else {
      // Clear state when field is null/undefined/empty
      setCurrentFileUrl('');
      setFileType('');
    }
  }, [options, field]);

  useEffect(() => {
    if (file) {
      handleUpload();
    }
  }, [file]);

  const handleChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Parse accepted file types from prop (e.g., "image/jpeg, image/png" or "audio/mpeg, audio/ogg")
    const allowedTypes = acceptedFileTypes.split(',').map(t => t.trim());
    
    // Validate file type against accepted types
    if (!allowedTypes.includes(selectedFile.type)) {
      // Generate user-friendly error message based on accepted types
      const isAudioUpload = acceptedFileTypes.includes('audio/');
      const isImageUpload = acceptedFileTypes.includes('image/');
      
      if (isAudioUpload && selectedFile.type.startsWith('image/')) {
        setErrorMessage('Please select an audio file, not an image.');
      } else if (isImageUpload && selectedFile.type.startsWith('audio/')) {
        setErrorMessage('Please select an image file, not an audio file.');
      } else if (isImageUpload) {
        setErrorMessage('Invalid file type. Only JPG, PNG, and SVG images are allowed.');
      } else if (isAudioUpload) {
        setErrorMessage('Invalid file type. Only MP3, OGG, and WAV audio files are allowed.');
      } else {
        setErrorMessage('Invalid file type.');
      }
      return;
    }

    // Determine if this is an audio or image upload
    const isAudioFile = selectedFile.type.startsWith('audio/');
    
    // File size restriction - 3MB for images, 5MB for audio
    const maxSizeInBytes = isAudioFile ? 5 * 1024 * 1024 : 3 * 1024 * 1024;
    if (selectedFile.size > maxSizeInBytes) {
      const sizeMB = (selectedFile.size / (1024 * 1024)).toFixed(1);
      const limitMB = isAudioFile ? '5' : '3';
      setErrorMessage(`File too large (${sizeMB}MB). Maximum is ${limitMB}MB.`);
      return;
    }

    try {
      setErrorMessage('');
      setUploading(true);
      
      // Handle audio files - no processing needed
      if (isAudioFile) {
        setFileType('audio');
        const reader = new FileReader();
        reader.onload = (event) => {
          setFile(event.target.result);
        };
        reader.readAsDataURL(selectedFile);
        return;
      }
      
      // Check if this is an SVG file
      const isSvg = selectedFile.type === 'image/svg+xml';
      setFileType(isSvg ? 'svg' : 'raster');
      
      if (isSvg) {
        // For SVGs, we can use them directly without processing
        const reader = new FileReader();
        reader.onload = (event) => {
          setFile(event.target.result);
        };
        reader.readAsDataURL(selectedFile);
      } else {
        // For raster images, process based on type
        // Skip processing if maxDimensions are very large (indicates sprite sheet or large asset)
        const skipProcessing = maxDimensions && (maxDimensions.width > 2000 || maxDimensions.height > 2000);
        // Keep PNG character/player assets at full resolution (no downscale) so they stay sharp like SVGs
        const isCharacterAsset = field === 'playerAnimationFrame' || field === 'playerJumpImage';
        const keepPngFullRes = isCharacterAsset && selectedFile.type === 'image/png';

        if (skipProcessing || keepPngFullRes) {
          // Use original file without resizing/re-encoding
          const reader = new FileReader();
          reader.onload = (event) => {
            setFile(event.target.result);
          };
          reader.readAsDataURL(selectedFile);
        } else {
          // Process normally for smaller images
          const isGameAsset = field === 'cubeImage' || field === 'playerImage';
          const isProgressiveReveal = field === 'imageUrl';
          
          let dimensions;
          if (isGameAsset) {
            dimensions = { width: 256, height: 256 };
          } else if (isProgressiveReveal) {
            dimensions = { width: 800, height: 800 };
          } else {
            dimensions = { width: 1200, height: 1200 };
          }
          
          const processedFile = await processImage(selectedFile, dimensions);
          setFile(processedFile);
        }
      }
    } catch (error) {
      console.error('Error processing file:', error);
      setErrorMessage('Error processing file. Please try again.');
      setUploading(false);
    }
  };

  const handleUpload = () => {
    if (!file) return;

    setUploading(true);
    // Use public/games directory only for public games
    const folderPath = isPublicGame ? `public/games/${field}` : field;
    
    // Determine file extension based on file type
    let fileExtension = 'jpg';
    if (fileType === 'svg') {
      fileExtension = 'svg';
    } else if (fileType === 'audio') {
      // Determine audio extension from the data URL
      if (file.includes('audio/mpeg')) fileExtension = 'mp3';
      else if (file.includes('audio/ogg')) fileExtension = 'ogg';
      else if (file.includes('audio/wav')) fileExtension = 'wav';
      else fileExtension = 'mp3'; // fallback
    }
    let safeFileName = sanitizeStorageFileName(file.name) || 'file';
    if (!safeFileName.includes('.')) safeFileName += `.${fileExtension}`;
    const storageReference = storageRef(storage, `${folderPath}/${Date.now()}_${safeFileName}`);
    
    // Convert data URL to blob for upload
    const blob = dataURLtoBlob(file);
    const uploadTask = uploadBytesResumable(storageReference, blob);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const prog = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        setProgress(prog);
      },
      (error) => {
        setUploading(false);
        setErrorMessage('Upload failed. Please try again.');
      },
      async () => {
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        setCurrentFileUrl(url);
        handleFileUploadSuccess(url);
        setUploading(false);
        // Flash a success label for ~1.6s so the upload feels confirmed.
        const successName = (file && file.name) ? file.name.replace(/^.*[\\/]/, '').slice(0, 40) : 'Upload complete';
        setJustUploadedName(successName);
        if (successTimerRef.current) clearTimeout(successTimerRef.current);
        successTimerRef.current = setTimeout(() => setJustUploadedName(''), 1600);
        
        // Save to custom assets if company ID is provided and it's an image
        if (companyId && !url.startsWith('audio/')) {
          try {
            const category = customAssetsService.getFieldCategory(field);
            await customAssetsService.saveAsset(companyId, {
              url,
              name: file.name || `Custom ${displayText || 'Asset'}`,
              category,
              field
            });
            pickerRef.current?.refetchCustomAssets?.();
          } catch (saveError) {
            // Don't fail the upload if saving to library fails
            console.warn('Could not save to custom assets:', saveError);
          }
        }
      }
    );
  };

  // Helper function to convert data URL to Blob
  const dataURLtoBlob = (dataURL) => {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  const handleFileUploadSuccess = (url) => {
    const updatedOptions = { ...options, [field]: url };
    if (effectiveSelectionNameKey) updatedOptions[effectiveSelectionNameKey] = null; // Custom upload clears library name
    applyOrOfferSync(updatedOptions);
  };

  const handleDelete = () => {
    setCurrentFileUrl(null);
    setProgress(0);
    setFileType('');
    fileInputRef.current.value = null; // Reset the input field
    const updatedOptions = { ...options, [field]: null };
    if (effectiveSelectionNameKey) updatedOptions[effectiveSelectionNameKey] = null;
    onDelete(updatedOptions);
  };
  const deleteHoverClasses = 'gch-text-secondary hover:gch-text-primary hover:bg-white/10 transition-colors';
  const deleteButtonClasses = `${deleteHoverClasses} border border-[var(--admin-border)]`;

  const syncOfferDialog = syncOfferOtherField && syncOfferOtherLabel && (
    <Transition.Root show={syncOfferOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => handleSyncOfferConfirm(false)}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" />
        </Transition.Child>
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-sm rounded-xl gch-bg-card p-5 shadow-xl border border-[var(--admin-border)]">
                <Dialog.Title as="h3" className="text-base font-semibold gch-text-primary">
                  Update other background?
                </Dialog.Title>
                <p className="mt-2 text-sm gch-text-secondary">
                  Do you want to also update {syncOfferOtherLabel} to match?
                </p>
                <div className="mt-4 flex gap-2 justify-end">
                  <button
                    type="button"
                    className="px-3 py-2 rounded-lg text-sm font-medium border border-[var(--admin-border)] gch-text-primary hover:bg-white/5"
                    onClick={() => handleSyncOfferConfirm(false)}
                  >
                    No
                  </button>
                  <button
                    type="button"
                    className="px-3 py-2 rounded-lg text-sm font-medium bg-[var(--admin-accent)] text-white hover:opacity-90"
                    onClick={() => handleSyncOfferConfirm(true)}
                  >
                    Yes
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );

  // Render compact mode: always two rows  -  row 1 = thumbnail + label, row 2 = buttons (full width, 50% each when empty)
  if (compact) {
    return (
      <>
      <div className="relative flex flex-col gap-3 p-3 rounded-lg border border-[var(--admin-border)] gch-input-ghost hover:border-[var(--admin-border)] transition-colors min-w-0">
        {/* Row 1: thumbnail + file info */}
        <div className="flex items-start gap-3 w-full min-w-0">
          <button
            type="button"
            onClick={() => hasLibraryAssets ? setShowAssetLibrary(true) : fileInputRef.current?.click()}
            className={`flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border transition-all ${
              currentFileUrl 
                ? 'bg-white/5 border-[var(--admin-border)] cursor-default' 
                : 'bg-white/5 border-dashed border-[var(--admin-border)] hover:border-[var(--admin-accent)] hover:bg-[var(--admin-accent-light)] cursor-pointer'
            }`}
            disabled={currentFileUrl && uploading}
          >
            {currentFileUrl ? (
              isProceduralBackground(currentFileUrl) ? (
                <ProceduralThumbnail rendererKey={getProceduralKey(currentFileUrl)} isStatic />
              ) : (
                <img
                  src={resolvePreviewUrl(currentFileUrl)}
                  alt="Uploaded"
                  className="w-full h-full object-contain bg-white/5"
                />
              )
            ) : (
              <div className="w-full h-full flex items-center justify-center gch-text-muted">
                {icon || <PhotoIcon className="w-6 h-6" />}
              </div>
            )}
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <h4 className="text-xs font-semibold gch-text-primary truncate" title={currentFileUrl && resolvedAssetName ? resolvedAssetName : displayText}>
                {currentFileUrl && resolvedAssetName ? resolvedAssetName : displayText}
              </h4>
              {tooltip && (
                <div className="relative inline-flex flex-shrink-0 group/tooltip">
                  <div className="p-1 -m-1 cursor-help">
                    <QuestionMarkCircleIcon className="w-4 h-4 gch-text-muted group-hover/tooltip:gch-text-secondary transition-colors" />
                  </div>
                  <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 opacity-0 invisible group-hover/tooltip:opacity-100 group-hover/tooltip:visible transition-all duration-150 z-[9999]">
                    <div className="bg-slate-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg min-w-[200px] max-w-xs whitespace-normal leading-relaxed">
                      {tooltip}
                    </div>
                    <div className="absolute left-1/2 -translate-x-1/2 top-full -mt-1">
                      <div className="border-4 border-transparent border-t-slate-900"></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {(subtitle || (effectiveSelectionNameKey && currentFileUrl)) && (
              <p className="text-xs gch-text-secondary truncate" title={currentFileUrl && resolvedAssetName ? 'Choose from library or upload' : subtitle}>
                {currentFileUrl && resolvedAssetName ? 'Choose from library or upload' : subtitle}
              </p>
            )}
            {uploading && (
              <div className="w-full rounded-full h-1 mt-1 bg-white/10">
                <div
                  className="h-1 rounded-full transition-all duration-300 bg-[var(--admin-accent)]"
                  style={{ width: `${progress}%` }}
                />
              </div>
            )}
            {!uploading && justUploadedName && (
              <div className="mt-1 inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-300 animate-[fadeIn_0.25s_ease-out]">
                <CheckIcon className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                <span className="truncate" title={justUploadedName}>Uploaded · {justUploadedName}</span>
              </div>
            )}
          </div>
        </div>

        {/* Row 2: buttons  -  Browse (opens library; upload is inside My Assets) or Upload when no library */}
        <div className="flex gap-2 w-full items-center [&_button]:flex-1 [&_button]:min-w-0 [&_button]:justify-center [&_button]:inline-flex [&_button]:items-center [&_button]:gap-1.5 [&_button]:py-2 [&_button]:rounded-md [&_button]:text-xs [&_button]:font-semibold">
          {currentFileUrl ? (
            <>
              {hasLibraryAssets && (
                <button
                  type="button"
                  className="bg-white/5 gch-text-primary hover:bg-white/10 border border-[var(--admin-border)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--admin-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
                  onClick={() => setShowAssetLibrary(true)}
                  title="Browse options"
                >
                  <Squares2X2Icon className="h-3.5 w-3.5 flex-shrink-0" />
                  <span className="whitespace-nowrap">Browse</span>
                </button>
              )}
              <button
                type="button"
                className={deleteButtonClasses}
                onClick={handleDelete}
                title="Remove"
              >
                <TrashIcon className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="whitespace-nowrap">Remove</span>
              </button>
            </>
          ) : (
            <>
              {hasLibraryAssets ? (
                <button
                  type="button"
                  className="bg-white/5 gch-text-primary hover:bg-white/10 border border-[var(--admin-border)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--admin-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
                  onClick={() => setShowAssetLibrary(true)}
                  title="Browse library or upload from My Assets"
                >
                  <Squares2X2Icon className="h-3.5 w-3.5 flex-shrink-0" />
                  <span className="whitespace-nowrap">Browse</span>
                </button>
              ) : (
                <button
                  type="button"
                  className="bg-white/5 gch-text-primary hover:bg-white/10 border border-[var(--admin-border)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--admin-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
                  onClick={() => fileInputRef.current?.click()}
                  title="Upload file"
                >
                  <ArrowUpTrayIcon className="h-3.5 w-3.5 flex-shrink-0" />
                  <span className="whitespace-nowrap">Upload</span>
                </button>
              )}
            </>
          )}
        </div>

        {errorMessage && (
          <div className="text-xs font-semibold text-red-600 sm:absolute sm:-bottom-5 sm:left-0">
            {errorMessage}
          </div>
        )}

        <input
          id={`${field}-upload`}
          name={`${field}-upload`}
          type="file"
          className="sr-only"
          ref={fileInputRef}
          onChange={handleChange}
          accept={acceptedFileTypes}
        />

        {/* Asset Library Picker Modal  -  upload lives in My Assets */}
        {hasLibraryAssets && (
          <AssetLibraryPicker
            ref={pickerRef}
            isOpen={showAssetLibrary}
            onClose={() => setShowAssetLibrary(false)}
            onSelect={handleLibrarySelect}
            field={field}
            gameType={gameType}
            currentValue={currentFileUrl}
            allowGroups={false}
            companyId={companyId}
            categoryOnly={fullLibrary ? false : restrictToCategoryOnly}
            onRequestUpload={companyId ? () => fileInputRef.current?.click() : undefined}
            allowMultipleSelect={allowMultipleSelect}
          />
        )}
      </div>
      {syncOfferDialog}
      </>
    );
  }

  // Render public game style
  if (isPublicGame) {
    return (
      <>
      <div className="p-4 rounded-lg border border-[var(--admin-border)] gch-input-ghost">
        {/* Header with icon/preview and title */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 rounded-lg flex-shrink-0 w-16 h-16 flex items-center justify-center bg-white/5 border border-[var(--admin-border)]">
              {currentFileUrl ? (
                isProceduralBackground(currentFileUrl) ? (
                  <ProceduralThumbnail rendererKey={getProceduralKey(currentFileUrl)} isStatic />
                ) : (
                  <img
                    src={resolvePreviewUrl(currentFileUrl)}
                    alt="Uploaded"
                    className="w-full h-full rounded-md object-contain bg-white/5"
                  />
                )
              ) : (
                <div className="gch-text-secondary">
                  {icon}
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold gch-text-primary">{displayText}</h3>
              {subtitle && (
                <p className="hidden sm:block text-xs font-medium mt-1 gch-text-secondary">{subtitle}</p>
              )}
            </div>
          </div>
          {subtitle && (
            <p className="sm:hidden text-xs font-medium gch-text-secondary break-words">{subtitle}</p>
          )}
        </div>

        {/* Action buttons underneath */}
        {currentFileUrl ? (
          <div className="flex gap-2">
            {hasLibraryAssets && (
              <button
                type="button"
                className="p-2.5 rounded-lg border border-[var(--admin-border)] bg-white/5 hover:bg-white/10 gch-text-primary transition-colors"
                onClick={() => setShowAssetLibrary(true)}
                title="Browse options"
              >
                <Squares2X2Icon className="h-4 w-4" />
              </button>
            )}
            {!hasLibraryAssets && (
              <button
                type="button"
                className="p-2.5 rounded-lg gch-input-ghost border border-[var(--admin-border)] gch-text-primary hover:bg-white/5 transition-colors"
                onClick={() => fileInputRef.current?.click()}
                title="Edit"
              >
                <PencilIcon className="h-4 w-4" />
              </button>
            )}
            <button
              type="button"
              className={`p-2.5 rounded-lg bg-white/5 ${deleteButtonClasses}`}
              onClick={handleDelete}
              title="Remove"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="flex gap-2">
            {hasLibraryAssets ? (
              <button
                type="button"
                className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold border-2 border-[var(--admin-accent)] bg-[var(--admin-accent-light)]/80 gch-text-primary hover:bg-[var(--admin-accent-light)] hover:border-[var(--admin-accent)] transition-colors"
                onClick={() => setShowAssetLibrary(true)}
              >
                <Squares2X2Icon className="h-4 w-4" />
                Browse
              </button>
            ) : (
              <button
                type="button"
                className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold bg-[var(--admin-accent)] text-white hover:bg-[var(--admin-accent)] transition-colors border border-[var(--admin-accent)] shadow-sm"
                onClick={() => fileInputRef.current?.click()}
              >
                <ArrowUpTrayIcon className="h-4 w-4" />
                Upload
              </button>
            )}
          </div>
        )}

        {uploading && (
          <div className="mt-3">
            <div className="w-full rounded-full h-2 bg-white/10">
              <div
                className="h-2 rounded-full transition-all duration-500 ease-in-out bg-[var(--admin-accent)]"
                style={{ 
                  width: `${progress}%`
                }}
              ></div>
            </div>
          </div>
        )}
        
        {errorMessage && (
          <div className="mt-2 text-xs font-semibold text-red-700">
            {errorMessage}
          </div>
        )}

        <input
          id={`${field}-upload`}
          name={`${field}-upload`}
          type="file"
          className="sr-only"
          ref={fileInputRef}
          onChange={handleChange}
          accept={acceptedFileTypes}
        />

        {/* Asset Library Picker Modal */}
        {hasLibraryAssets && (
          <AssetLibraryPicker
            ref={pickerRef}
            isOpen={showAssetLibrary}
            onClose={() => setShowAssetLibrary(false)}
            onSelect={handleLibrarySelect}
            field={field}
            gameType={gameType}
            currentValue={currentFileUrl}
            allowGroups={false}
            companyId={companyId}
            categoryOnly={fullLibrary ? false : restrictToCategoryOnly}
            onRequestUpload={companyId ? () => fileInputRef.current?.click() : undefined}
            allowMultipleSelect={allowMultipleSelect}
          />
        )}
      </div>
      {syncOfferDialog}
      </>
    );
  }

  // Original design for non-public games (same display logic as compact: show selection name when from library)
  const displayTitle = currentFileUrl && effectiveSelectionNameKey && options?.[effectiveSelectionNameKey] ? options[effectiveSelectionNameKey] : displayText;
  const displaySubtitle = currentFileUrl && effectiveSelectionNameKey && options?.[effectiveSelectionNameKey] ? 'Choose from library or upload' : subtitle;

  return (
    <>
    <div className="flex flex-col items-start w-full">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between w-full gap-1 sm:gap-2 mb-2">
        <span className="flex flex-grow flex-col min-w-0">
          <span className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold leading-6 gch-text-primary" style={{
              fontFamily: '"Inter", sans-serif'
            }}>
              {displayTitle}
            </span>
            {recommended && (
              <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ring-1 ring-inset bg-green-50 text-green-700 border border-green-300">
                Recommended
              </span>
            )}
          </span>
          {displaySubtitle && (
            <>
              <span className="hidden sm:inline text-sm font-medium gch-text-secondary" style={{
                fontFamily: '"Inter", sans-serif'
              }}>
                {displaySubtitle}
              </span>
              <span className="sm:hidden text-sm font-medium gch-text-secondary break-words" style={{
                fontFamily: '"Inter", sans-serif'
              }}>
                {displaySubtitle}
              </span>
            </>
          )}
        </span>
      </div>

      {currentFileUrl && displayPreview && (
        <div className="relative mb-4 w-full">
          <div className={`relative rounded-lg overflow-hidden ${
            field === 'cubeImage' || field === 'playerImage' || field === 'sharingPhoto'
              ? 'w-20 h-20' 
              : field === 'coverPhoto'
                ? 'w-32 aspect-[1.91/1] bg-white/5'
                : 'max-w-24 max-h-24'
          }`}>
            {isProceduralBackground(currentFileUrl) ? (
              <ProceduralThumbnail rendererKey={getProceduralKey(currentFileUrl)} isStatic />
            ) : (
              <img
                src={resolvePreviewUrl(currentFileUrl)}
                alt="Uploaded file"
                className="rounded-lg w-full h-full object-contain bg-white/5"
              />
            )}
            <div className="absolute top-1.5 right-1.5 flex gap-1.5 z-10">
              {hasLibraryAssets && (
                <button
                  type="button"
                  className="p-2.5 bg-white/90 backdrop-blur-sm rounded-lg shadow-sm text-admin-icon hover:text-admin-icon-hover transition-colors"
                  onClick={() => setShowAssetLibrary(true)}
                  title="Browse options"
                >
                  <Squares2X2Icon className="h-4 w-4" />
                </button>
              )}
              {!hasLibraryAssets && (
                <button
                  type="button"
                  className="p-2.5 bg-white/90 backdrop-blur-sm rounded-lg shadow-sm gch-text-secondary hover:gch-text-primary transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                  title="Upload Custom"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
              )}
              <button
                type="button"
                className={`p-2.5 bg-white/90 backdrop-blur-sm rounded-lg shadow-sm ${deleteButtonClasses}`}
                onClick={handleDelete}
                title="Remove"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {(!currentFileUrl || !displayPreview) && (
        <div 
          className={`relative z-0 w-full mt-2 flex justify-center rounded-lg border-2 border-dashed px-6 py-10 transition-colors border-[var(--admin-border)] bg-white/5 ${
            field === 'cubeImage' || field === 'playerImage' || field === 'sharingPhoto' ? 'max-w-xs' : ''
          }`}
        >
          <div className="text-center">
            <PhotoIcon className="mx-auto h-12 w-12 gch-text-muted" />
            <div className="mt-4 flex flex-col items-center gap-2">
              <div className="flex gap-2">
                {hasLibraryAssets ? (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 rounded-lg text-xs px-3.5 py-2.5 font-semibold shadow-sm transition-colors border-2 border-[var(--admin-accent)] bg-[var(--admin-accent-light)]/80 gch-text-primary hover:bg-[var(--admin-accent-light)] hover:border-[var(--admin-accent)]"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowAssetLibrary(true);
                    }}
                  >
                    <Squares2X2Icon className="h-4 w-4" />
                    Browse
                  </button>
                ) : (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 rounded-lg text-xs px-3.5 py-2.5 font-semibold shadow-sm transition-colors bg-[var(--admin-accent)] text-white hover:bg-[var(--admin-accent)] border border-[var(--admin-accent)]"
                    onClick={(e) => {
                      e.stopPropagation();
                      fileInputRef.current?.click();
                    }}
                  >
                    <ArrowUpTrayIcon className="h-4 w-4" />
                    Upload
                  </button>
                )}
              </div>
              <p className="mt-1 text-xs gch-text-secondary font-medium" style={{
                fontFamily: '"Inter", sans-serif'
              }}>
                {hasLibraryAssets ? 'Browse library  -  upload from My Assets' : 'Choose from library or upload.'}
              </p>
            </div>
          </div>
        </div>
      )}

      <input
        id={`${field}-upload`}
        name={`${field}-upload`}
        type="file"
        className="sr-only"
        ref={fileInputRef}
        onChange={handleChange}
        accept={acceptedFileTypes}
        compact={true}
      />
      
      {uploading && (
        <div className="w-full rounded-full h-1.5 mt-2 bg-white/10">
          <div
            className="h-1.5 rounded-full transition-all duration-500 ease-in-out bg-[var(--admin-accent)]"
            style={{ 
              width: `${progress}%`
            }}
          ></div>
        </div>
      )}
      
      {errorMessage && (
        <div className="mt-2 text-sm font-semibold text-red-700" style={{
          fontFamily: '"Inter", sans-serif'
        }}>
          {errorMessage}
        </div>
      )}

      {/* Asset Library Picker Modal  -  upload lives in My Assets */}
      {hasLibraryAssets && (
        <AssetLibraryPicker
          ref={pickerRef}
          isOpen={showAssetLibrary}
          onClose={() => setShowAssetLibrary(false)}
          onSelect={handleLibrarySelect}
          field={field}
          gameType={gameType}
          currentValue={currentFileUrl}
          allowGroups={false}
          companyId={companyId}
          categoryOnly={fullLibrary ? false : restrictToCategoryOnly}
          onRequestUpload={companyId ? () => fileInputRef.current?.click() : undefined}
          allowMultipleSelect={allowMultipleSelect}
        />
      )}
    </div>
    {syncOfferDialog}
    </>
  );
};

// Public-mode wrapper: hides custom asset upload UI for non-admin users
// (SaaS v1 simplification). Admins still see the full FileUpload component
// so internal / advanced flows keep working.
const PublicGatedFileUpload = (props) => {
  const { isAdmin } = useUser();
  if (!isAdmin && !PUBLIC_FIELD_ALLOWLIST.has(props.field)) {
    return null;
  }
  return <FileUpload {...props} />;
};

export default PublicGatedFileUpload;
